import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Alert,
  Spin,
  Typography,
  Space,
  Divider,
  message,
  Form,
  Input,
  Modal,
} from "antd";
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  TeamOutlined,
  CrownOutlined,
  LogoutOutlined,
  CustomerServiceOutlined,
  KeyOutlined,
  LoginOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useRoles } from "../contexts/role-context";
import { useOrganization } from "../contexts/organization-context";
import { useSubscription } from "../contexts/subscription-context";
import { useLocation } from "react-router-dom";
import { CreateOrganizationModal } from "./CreateOrganizationModal";
import apiClient from "../utils/axios";

const { Title, Text, Paragraph } = Typography;

interface AccessStatus {
  has_access: boolean;
  reason?: string;
  organizations: Array<{
    id: string;
    name: string;
    subscription_status?: string;
    free_trial_expires_at?: string;
    is_owner: boolean;
  }>;
  needs_organization: boolean;
  user_trial_expires_at?: string;
  trial_days_remaining?: number;
}

interface AccessControlProps {
  children: React.ReactNode;
}

export const AccessControl: React.FC<AccessControlProps> = ({ children }) => {
  const { logout, getAccessTokenSilently } = useAuth0();
  const { roles } = useRoles();
  const { refreshOrganizations } = useOrganization();
  const { checkSubscription } = useSubscription();
  const location = useLocation();
  const [accessStatus, setAccessStatus] = useState<AccessStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Check if this is a facility invitation callback
  const isFacilityInvitationCallback = () => {
    const searchParams = new URLSearchParams(location.search);
    const isAuthCallback = location.pathname === '/auth/callback';
    const invitationType = searchParams.get('type');
    const facilityId = searchParams.get('facility');
    
    return isAuthCallback && invitationType === 'facility_invite' && facilityId;
  };

  // Check if we should bypass access controls
  const shouldBypassAccessControls = isFacilityInvitationCallback();

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const checkAccess = async () => {
    try {
      setLoading(true);
      
      // Get the Auth0 token
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get("/api/access/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAccessStatus(response.data);
    } catch (error) {
      console.error("Error checking access:", error);
      message.error("Failed to check access status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAccess();
  }, []);

  const handleCreateSuccess = async () => {
    setShowCreateModal(false);
    
    // Refresh contexts and access status
    await Promise.all([
      refreshOrganizations(),
      checkSubscription(),
      checkAccess()
    ]);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!accessStatus) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Alert
          message="Access Check Failed"
          description="Unable to verify your access status. Please try refreshing the page."
          type="error"
          showIcon
          action={
            <Button onClick={checkAccess} type="primary">
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  // Allow access if user has any organization, valid subscription, or active trial
  if (accessStatus.has_access || roles.includes("Admin")) {
    return <>{children}</>;
  }

  // User doesn't have access - show access denied with options
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "#f5f5f5",
        padding: "20px",
      }}
    >
      <Card
        style={{
          maxWidth: 500,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <LockOutlined
              style={{
                fontSize: "48px",
                color: "#1890ff",
                marginBottom: "16px",
              }}
            />
            <Title level={2} style={{ marginBottom: "8px" }}>
              Access Required
            </Title>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              You need to be part of an organization to access CodePilot
            </Text>
          </div>

          <Alert
            message="Getting Started"
            description={
              <div style={{ textAlign: "left" }}>
                <Paragraph style={{ marginBottom: "12px" }}>
                  To access CodePilot, you can:
                </Paragraph>
                <ul style={{ paddingLeft: "20px", margin: 0 }}>
                  <li><strong>Create a new organization</strong> - Get a 14-day free trial</li>
                  <li><strong>Ask your team for an email invitation</strong> - They can invite you from the Facilities page</li>
                </ul>
              </div>
            }
            type="info"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ textAlign: "left" }}
          />
          
          {accessStatus?.trial_days_remaining !== undefined && accessStatus.trial_days_remaining > 0 && (
            <Alert
              message={`${accessStatus.trial_days_remaining} Days Left in Your Trial`}
              description="Create or join an organization to continue after your trial ends."
              type="warning"
              showIcon
              icon={<CrownOutlined />}
            />
          )}
          
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setShowCreateModal(true)}
              style={{ width: "100%" }}
            >
              Create New Organization
            </Button>
          </Space>
          
          <Button
            type="text"
            onClick={handleLogout}
            style={{ width: "100%", marginTop: "8px" }}
          >
            Switch Account
          </Button>
        </Space>

        <CreateOrganizationModal
          open={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      </Card>
    </div>
  );
}; 